import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { useTranslations } from '@/hooks/useTranslations';
import type { AdocEntrySummary } from '@/types/adoc';
import { Download, ExternalLink } from 'lucide-react';
import { route } from 'ziggy-js';

interface AdocPreviewDialogProps {
    fileId: string;
    entry: AdocEntrySummary | null;
    onClose: () => void;
}

/**
 * PDF preview for one document inside the container.
 *
 * `variant="dialog"` per the design system: media previews stay a centred dialog
 * on desktop and a bottom drawer on mobile.
 */
export function AdocPreviewDialog({ fileId, entry, onClose }: AdocPreviewDialogProps) {
    const { t } = useTranslations();

    const previewUrl = entry ? route('adoc.entries.preview', { adocFile: fileId, entry: entry.index }) : null;

    return (
        <ResponsiveModal open={entry !== null} onOpenChange={(open) => (open ? undefined : onClose())} variant="dialog">
            <ResponsiveModalContent dialogClassName="sm:max-w-[64rem]" className="max-h-[92dvh]">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="truncate">
                        {entry?.name ?? t('adoc.show.preview.title', 'Document preview')}
                    </ResponsiveModalTitle>
                </ResponsiveModalHeader>

                <ResponsiveModalBody className="p-0">
                    {previewUrl ? (
                        <>
                            <div className="flex flex-wrap justify-end gap-2 px-4 pb-3">
                                {/* iOS Safari will not scroll a PDF in an iframe, so a plain
                                    link out to the file stays available on every platform. */}
                                <Button asChild variant="outline" size="sm">
                                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                                        <ExternalLink />
                                        {t('adoc.show.preview.open_in_tab', 'Open in a new tab')}
                                    </a>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                    <a href={route('adoc.entries.download', { adocFile: fileId, entry: entry!.index })}>
                                        <Download />
                                        {t('adoc.show.documents.download', 'Download')}
                                    </a>
                                </Button>
                            </div>
                            <iframe src={`${previewUrl}#toolbar=1`} title={entry?.name ?? ''} className="h-[70dvh] w-full border-0" />
                        </>
                    ) : null}
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
